import { BackendSource, backendSources } from "@/shared/api/firehose";
import { generateRandomColor } from "@/utils/other";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export type FontSize = "small" | "medium" | "large" | "extra-large" | "extra-extra-large";

export type Theme = "dark" | "light";

export type ImportantHeadlineColorKey =
  | "terminal-orange"
  | "wire-red"
  | "amber"
  | "midnight"
  | "emerald"
  | "slate"
  | "cyan"
  | "magenta";

export const importantHeadlineColorPresets: Record<
  ImportantHeadlineColorKey,
  { label: string; bg: string; text: string }
> = {
  "terminal-orange": { label: "Terminal Orange (Default)", bg: "#FF6600", text: "#000000" },
  "wire-red": { label: "Wire Red", bg: "#B91C1C", text: "#FFFFFF" },
  amber: { label: "Amber", bg: "#D97706", text: "#000000" },
  midnight: { label: "Midnight", bg: "#1E3A5F", text: "#FFFFFF" },
  emerald: { label: "Emerald", bg: "#047857", text: "#FFFFFF" },
  slate: { label: "Slate", bg: "#334155", text: "#F8FAFC" },
  cyan: { label: "Cyan", bg: "#0E7490", text: "#FFFFFF" },
  magenta: { label: "Magenta", bg: "#9D174D", text: "#FFFFFF" },
};

type SettingsStore = {
  notificationsSoundEnabled: boolean;

  sourceColors: Record<BackendSource, string>;

  fontSize: FontSize;

  theme: Theme;

  importantHeadlineColor: ImportantHeadlineColorKey;

  init: (state: typeof initialState) => void;

  toggleNotificationsSound: () => void;

  setSourceColor: (source: BackendSource, color: string) => void;

  setFontSize: (size: FontSize) => void;

  setTheme: (theme: Theme) => void;

  setImportantHeadlineColor: (color: ImportantHeadlineColorKey) => void;
};

const defaultSourceColors = backendSources.reduce((acc, source) => {
  acc[source] = generateRandomColor();
  return acc;
}, {} as Record<BackendSource, string>);

const initialState = {
  notificationsSoundEnabled: true,

  sourceColors: defaultSourceColors,

  fontSize: "medium" as FontSize,

  theme: "dark" as Theme,

  importantHeadlineColor: "terminal-orange" as ImportantHeadlineColorKey,
};

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      ...initialState,

      init: (initialState) => set({ ...initialState }),

      toggleNotificationsSound: () =>
        set((state) => ({
          notificationsSoundEnabled: !state.notificationsSoundEnabled,
        })),

      setSourceColor: (source, color) =>
        set((state) => ({
          sourceColors: {
            ...state.sourceColors,
            [source]: color,
          },
        })),

      setFontSize: (size) =>
        set(() => ({
          fontSize: size,
        })),

      setTheme: (theme) =>
        set(() => ({
          theme: theme,
        })),

      setImportantHeadlineColor: (color) =>
        set(() => ({
          importantHeadlineColor: color,
        })),
    }),
    {
      name: "settings-storage",
    }
  )
);
